import { useCallback, useEffect, useState } from 'react';
import { sparkToast } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import { getCloudDeployStatus, requestCloudDeploy } from '../api/customerIntentsApi';
import { CLOUD_DEPLOY_SENT_MESSAGE, CLOUD_DEPLOY_SENT_TITLE } from '../constants';

/**
 * "I want to deploy platform on my cloud" — the SaaS-only last row of the
 * "Need a Dataset?" menu (Figma 9293-3459).
 *
 * - `available`: show the row (SaaS with customer intents configured).
 * - `disabled`: the signed-in user already asked (stored server-side) or a
 *   request is in flight — rendered grey per Figma 9347-2466.
 * - `request()`: POST once, success toast (Figma 9293-3254), then disable.
 */
export default function useCloudDeployRequest() {
    const auth = useAuth();
    const available = auth?.isSaas === true && auth?.customerIntentsEnabled === true;
    const workspace = auth?.workspace;
    const [requested, setRequested] = useState(false);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (!available) return undefined;
        let cancelled = false;
        getCloudDeployStatus().then((status) => {
            if (!cancelled) setRequested(status.requested);
        });
        return () => {
            cancelled = true;
        };
    }, [available]);

    const request = useCallback(async () => {
        if (!available || requested || pending) return;
        setPending(true);
        try {
            const status = await requestCloudDeploy({
                workspace: workspace?.name ?? workspace?.id ?? null,
            });
            setRequested(status.requested);
            // `detailed` is what makes Spark render the body line under the title.
            sparkToast.success(CLOUD_DEPLOY_SENT_TITLE, {
                content: CLOUD_DEPLOY_SENT_MESSAGE,
                detailed: true,
            });
        } catch (err) {
            sparkToast.error(err?.detail || err?.message);
        } finally {
            setPending(false);
        }
    }, [available, requested, pending, workspace]);

    return { available, requested, disabled: requested || pending, request };
}
